import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { useChurchPlanner } from '../../hooks/useChurchPlanner';
import { hasAnyPermission, Permission } from '../../utils/roles';
import {
  PlannerPage,
  PlannerCard,
  PlannerKicker,
  PlannerHeading,
  PlannerSubtle,
  PlannerError,
  plannerColors,
} from './plannerTheme';
import {
  WEEKLY_REPEAT_COUNTS,
  addServiceDate as appendServiceDate,
  addWeeklyDates,
  formatDateChip,
  sortDatesAscending,
  toDateKey,
} from './servicePlanCreateDates';

const CreateWrap = styled.div`
  width: min(100%, 760px);
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.75);
`;

const Input = styled.input`
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  color: #fff;
  padding: 0.55rem 0.65rem;
  font-size: 0.9rem;
`;

const Select = styled.select`
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  color: #fff;
  padding: 0.55rem 0.65rem;
  font-size: 0.9rem;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;

  .ant-picker-input > input {
    color: #fff;
  }
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.55rem;
`;

const QuickFillPill = styled.button<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? '#f4cb4f' : 'rgba(255, 255, 255, 0.2)')};
  border-radius: 999px;
  background: ${({ $active }) => ($active ? 'rgba(244, 203, 79, 0.2)' : 'rgba(255, 255, 255, 0.05)')};
  color: #fff;
  font-size: 0.72rem;
  padding: 0.24rem 0.58rem;
  cursor: pointer;
`;

const Textarea = styled.textarea`
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  color: #fff;
  padding: 0.55rem 0.65rem;
  min-height: 88px;
  font-size: 0.9rem;
  resize: vertical;
`;

const Action = styled.button`
  border: none;
  border-radius: 10px;
  background: #f4cb4f;
  color: #161515;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.55rem 0.85rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Feedback = styled.div<{ $error: boolean }>`
  margin-top: 0.9rem;
  border: 1px solid ${({ $error }) => ($error ? 'rgba(255, 69, 58, 0.4)' : plannerColors.border)};
  border-radius: 12px;
  padding: 0.8rem 0.9rem;
  background: ${({ $error }) => ($error ? 'rgba(255, 69, 58, 0.12)' : plannerColors.surfaceHover)};
  color: ${({ $error }) => ($error ? '#ffd8d5' : plannerColors.text)};
  font-size: 0.88rem;
`;

const SelectedDatesWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const SelectedDateChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid rgba(244, 203, 79, 0.35);
  border-radius: 999px;
  background: rgba(244, 203, 79, 0.14);
  color: #fff3c4;
  padding: 0.3rem 0.65rem;
  font-size: 0.78rem;
`;

const ChipRemoveButton = styled.button`
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 0.82rem;
  line-height: 1;
  padding: 0;
`;

const HelperText = styled.div`
  margin-top: 0.45rem;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.78rem;
`;

const TITLE_QUICK_FILLS = [
  '8am Worship',
  '9:30 Worship',
  '11am Worship',
];

const START_TIME_QUICK_FILLS = [
  { label: '7:00am', value: '07:00' },
  { label: '9:30am', value: '09:30' },
  { label: '11:00am', value: '11:00' },
];

export default function ChurchPlannerCreateServicePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const planner = useChurchPlanner(true, user?.id);
  const getPlanById = planner.getPlanById;
  const sourcePlanId = searchParams.get('sourcePlanId')?.trim() || '';
  const isDuplicateMode = Boolean(sourcePlanId);

  const [title, setTitle] = useState('');
  const [serviceDate, setServiceDate] = useState<Dayjs | null>(null);
  const [serviceDates, setServiceDates] = useState<Dayjs[]>([]);
  const [startTime, setStartTime] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [feedback, setFeedback] = useState<{ message: string; error: boolean } | null>(null);
  const [loadingSourcePlan, setLoadingSourcePlan] = useState(false);
  const [sourcePlanTitle, setSourcePlanTitle] = useState('');
  const [sourcePlanReady, setSourcePlanReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canCreatePlans = hasAnyPermission(roles, [Permission.CREATE_CHURCH_PLAN]);
  const quickLocation = 'Main Campus';
  const selectedStartTime = startTime;
  const formDisabled = !canCreatePlans || loadingSourcePlan || submitting;

  const buildServiceDateIso = (selectedDate: Dayjs, timeValue: string) => {
    const [hourText, minuteText] = timeValue.trim().split(':');
    const hours = Number.parseInt(hourText, 10);
    const minutes = Number.parseInt(minuteText, 10);
    return selectedDate.hour(hours).minute(minutes).second(0).millisecond(0).toISOString();
  };

  const buildStartTimePickerValue = (timeValue: string): Dayjs | null => {
    if (!/^\d{2}:\d{2}$/.test(timeValue.trim())) return null;
    const [hourText, minuteText] = timeValue.trim().split(':');
    const hours = Number.parseInt(hourText, 10);
    const minutes = Number.parseInt(minuteText, 10);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
  };

  const handleStartTimeQuickFill = (nextTime: string) => {
    const [hourText, minuteText] = nextTime.split(':');
    const hours = Number.parseInt(hourText, 10);
    const minutes = Number.parseInt(minuteText, 10);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return;
    setStartTime(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
  };

  useEffect(() => {
    if (!isDuplicateMode || !sourcePlanId) return;

    let isMounted = true;

    const loadSourcePlan = async () => {
      setLoadingSourcePlan(true);
      setSourcePlanReady(false);
      try {
        const sourcePlan = await getPlanById(sourcePlanId);
        if (!isMounted) return;

        if (!sourcePlan) {
          setFeedback({ message: 'Source service could not be loaded.', error: true });
          setSourcePlanTitle('');
          return;
        }

        const sourceDate = dayjs(sourcePlan.service_date);
        const fallbackStartTime = sourceDate.isValid() ? sourceDate.format('HH:mm') : '';

        setSourcePlanTitle(sourcePlan.title || '');
        setTitle(sourcePlan.title || '');
        setLocation(sourcePlan.location || '');
        setNotes(sourcePlan.notes || '');
        setStartTime((sourcePlan.start_time || '').slice(0, 5) || fallbackStartTime);
        setSelectedTemplateId('');
        setSourcePlanReady(true);
        setFeedback(null);
      } catch (error) {
        if (!isMounted) return;
        setSourcePlanTitle('');
        setSourcePlanReady(false);
        setFeedback({
          message: error instanceof Error ? error.message : 'Unable to load the source service.',
          error: true,
        });
      } finally {
        if (isMounted) {
          setLoadingSourcePlan(false);
        }
      }
    };

    void loadSourcePlan();

    return () => {
      isMounted = false;
    };
  }, [getPlanById, isDuplicateMode, sourcePlanId]);

  const addServiceDate = (nextDate: Dayjs) => {
    setServiceDates((current) => appendServiceDate(current, nextDate));
  };

  const addWeeklyServiceDates = (weekCount: number) => {
    setServiceDates((current) => addWeeklyDates(current, weekCount));
  };

  const removeServiceDate = (dateKey: string) => {
    setServiceDates((current) => current.filter((entry) => toDateKey(entry) !== dateKey));
  };

  const handleCreatePlan = async () => {
    if (!canCreatePlans) return;
    if (!title.trim()) {
      setFeedback({ message: 'Plan title is required.', error: true });
      return;
    }
    if (isDuplicateMode) {
      if (!sourcePlanId) {
        setFeedback({ message: 'A source service is required for duplication.', error: true });
        return;
      }
      if (!sourcePlanReady) {
        setFeedback({ message: 'Source service could not be loaded.', error: true });
        return;
      }
    }
    if (!serviceDates.length) {
      setFeedback({
        message: isDuplicateMode ? 'Select at least one destination date.' : 'Select at least one service date.',
        error: true,
      });
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(startTime.trim())) {
      setFeedback({ message: 'Start time is required.', error: true });
      return;
    }

    setSubmitting(true);
    setFeedback(null);

    try {
      const targetDates = sortDatesAscending(serviceDates);
      let createdCount = 0;
      let lastCreatedPlanId = '';
      const createFailures: string[] = [];
      const copyFailures: string[] = [];

      for (const targetDate of targetDates) {
        const dateLabel = formatDateChip(targetDate);

        try {
          const createdPlan = await planner.createPlan({
            title: title.trim(),
            service_date: buildServiceDateIso(targetDate, startTime),
            start_time: `${startTime.trim()}:00`,
            location: location.trim() || null,
            notes: notes.trim() || null,
            created_by: user?.id || null,
          });

          createdCount += 1;
          lastCreatedPlanId = createdPlan.id;

          if (isDuplicateMode) {
            try {
              await planner.duplicatePlanStructure({
                source_plan_id: sourcePlanId,
                target_plan_id: createdPlan.id,
              });
            } catch (error) {
              console.error('Unable to duplicate service plan structure:', error);
              copyFailures.push(dateLabel);
            }
            continue;
          }

          if (selectedTemplateId) {
            try {
              await planner.applyServiceTemplate({
                service_plan_id: createdPlan.id,
                template_id: selectedTemplateId,
              });
            } catch (error) {
              console.error('Unable to apply service template:', error);
              copyFailures.push(dateLabel);
            }
          }
        } catch (error) {
          console.error('Unable to create service plan:', error);
          createFailures.push(dateLabel);
        }
      }

      if (!createdCount) {
        setFeedback({
          message: isDuplicateMode ? 'Unable to duplicate this service.' : 'Unable to create service plan.',
          error: true,
        });
        return;
      }

      if (createFailures.length || copyFailures.length) {
        const createMessage = createFailures.length
          ? `Could not create: ${createFailures.join(', ')}.`
          : null;
        const copyMessage = copyFailures.length
          ? isDuplicateMode
            ? `Created without copied order: ${copyFailures.join(', ')}.`
            : `Created without template applied: ${copyFailures.join(', ')}.`
          : null;
        window.alert([createMessage, copyMessage].filter(Boolean).join(' '));
      }

      setTitle('');
      setServiceDate(null);
      setServiceDates([]);
      setStartTime('');
      setLocation('');
      setNotes('');
      setSelectedTemplateId('');

      if (isDuplicateMode) {
        navigate(`/church-planner/services/${sourcePlanId}`);
        return;
      }

      if (createdCount > 1) {
        navigate('/church-planner/dashboard');
        return;
      }

      setFeedback({ message: 'Service plan created.', error: false });
      if (lastCreatedPlanId) {
        navigate(`/church-planner/services/${lastCreatedPlanId}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCreatePlans) {
    return (
      <PlannerPage>
        <CreateWrap>
          <PlannerCard>
            <PlannerKicker>Planner Admin</PlannerKicker>
            <PlannerHeading>Access Required</PlannerHeading>
            <PlannerError>Create plan permission is required to add a new service.</PlannerError>
          </PlannerCard>
        </CreateWrap>
      </PlannerPage>
    );
  }

  return (
    <PlannerPage>
      <CreateWrap>
        <PlannerCard>
          <PlannerKicker>Planner Admin</PlannerKicker>
          <PlannerHeading>{isDuplicateMode ? 'Duplicate Service Across Dates' : 'Create Service Plan'}</PlannerHeading>
          <PlannerSubtle>
            {isDuplicateMode
              ? loadingSourcePlan
                ? 'Loading the source service details...'
                : `Copy ${sourcePlanTitle ? `"${sourcePlanTitle}"` : 'this service'} into one or more new dates.`
              : 'Create one or more services with the same details and an optional template.'}
          </PlannerSubtle>

          <Field style={{ marginTop: '0.8rem' }}>
            Title
            <Input value={title} onChange={(event) => setTitle(event.target.value)} disabled={formDisabled} />
            <PillRow>
              {TITLE_QUICK_FILLS.map((quickFill) => (
                <QuickFillPill
                  key={quickFill}
                  type="button"
                  onClick={() => setTitle(quickFill)}
                  disabled={formDisabled}
                  $active={title.trim().toLowerCase() === quickFill.toLowerCase()}
                >
                  {quickFill}
                </QuickFillPill>
              ))}
            </PillRow>
          </Field>

          <Field style={{ marginTop: '0.6rem' }}>
            {isDuplicateMode ? 'Destination Dates' : 'Service Dates'}
            <StyledDatePicker
              format="MMM D, YYYY"
              value={serviceDate}
              onChange={(value) => {
                const nextValue = (value as Dayjs | null) ?? null;
                if (!nextValue) return;
                addServiceDate(nextValue);
                setServiceDate(null);
              }}
              disabled={formDisabled}
            />
            <HelperText>
              {isDuplicateMode
                ? 'Select a date to add it to the duplicate list.'
                : 'Select a date to add it. Weekly options fill that same weekday.'}
            </HelperText>
            {!isDuplicateMode && serviceDates.length ? (
              <PillRow>
                {WEEKLY_REPEAT_COUNTS.map((weekCount) => (
                  <QuickFillPill
                    key={weekCount}
                    type="button"
                    onClick={() => addWeeklyServiceDates(weekCount)}
                    disabled={formDisabled}
                  >
                    {`Next ${weekCount} weeks`}
                  </QuickFillPill>
                ))}
              </PillRow>
            ) : null}
            {serviceDates.length ? (
              <SelectedDatesWrap>
                {serviceDates.map((entry) => {
                  const dateKey = toDateKey(entry);
                  const label = formatDateChip(entry);
                  return (
                    <SelectedDateChip key={dateKey}>
                      {label}
                      <ChipRemoveButton
                        type="button"
                        onClick={() => removeServiceDate(dateKey)}
                        aria-label={`Remove ${label}`}
                        disabled={formDisabled}
                      >
                        x
                      </ChipRemoveButton>
                    </SelectedDateChip>
                  );
                })}
              </SelectedDatesWrap>
            ) : null}
          </Field>

          <Field style={{ marginTop: '0.6rem' }}>
            Start Time
            <StyledDatePicker
              picker="time"
              format="h:mm A"
              value={buildStartTimePickerValue(startTime)}
              onChange={(value) => {
                const nextValue = (value as Dayjs | null)?.format('HH:mm') ?? '';
                setStartTime(nextValue);
              }}
              disabled={formDisabled}
            />
            <PillRow>
              {START_TIME_QUICK_FILLS.map((quickFill) => (
                <QuickFillPill
                  key={quickFill.value}
                  type="button"
                  onClick={() => handleStartTimeQuickFill(quickFill.value)}
                  disabled={formDisabled}
                  $active={selectedStartTime === quickFill.value}
                >
                  {quickFill.label}
                </QuickFillPill>
              ))}
            </PillRow>
          </Field>

          <Field style={{ marginTop: '0.6rem' }}>
            Location
            <Input
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              disabled={formDisabled}
            />
            <PillRow>
              <QuickFillPill
                type="button"
                onClick={() => setLocation(quickLocation)}
                disabled={formDisabled}
                $active={location.trim().toLowerCase() === quickLocation.toLowerCase()}
              >
                Main Campus
              </QuickFillPill>
            </PillRow>
          </Field>

          <Field style={{ marginTop: '0.6rem' }}>
            Notes
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={formDisabled}
            />
          </Field>

          {!isDuplicateMode ? (
            <Field style={{ marginTop: '0.6rem' }}>
              Service Template (optional)
              <Select
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
                disabled={formDisabled || planner.templatesQuery.isLoading}
              >
                <option value="">No template (blank service)</option>
                {planner.serviceTemplates.map((template) => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </Select>
            </Field>
          ) : null}

          <Action
            type="button"
            style={{ marginTop: '0.8rem' }}
            onClick={handleCreatePlan}
            disabled={formDisabled || (isDuplicateMode && !sourcePlanReady)}
          >
            {loadingSourcePlan
              ? 'Loading...'
              : submitting
                ? 'Saving...'
                : isDuplicateMode
                  ? 'Duplicate Service'
                  : serviceDates.length > 1
                    ? `Create ${serviceDates.length} Plans`
                    : 'Create Plan'}
          </Action>

          {feedback ? <Feedback $error={feedback.error}>{feedback.message}</Feedback> : null}
        </PlannerCard>
      </CreateWrap>
    </PlannerPage>
  );
}